/**
 * Nova Q cart bridge.
 *
 * The custom NQ sections (header, product page, product sliders) add to cart with plain fetch calls.
 * This module routes those adds through Horizon's standard cart events so the built-in
 * cart drawer re-renders and opens, and keeps the NQ header badge in sync.
 *
 * Usage (from any script): window.NQCart.add(formDataOrItems, sourceElement) -> Promise<cart>
 *                          window.NQCart.open()
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';

function drawer() {
  return /** @type {any} */ (document.getElementById('cart-drawer'));
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
}

function updateBadges(count) {
  document.querySelectorAll('.nqh-badge,[data-cart-count]').forEach((el) => {
    el.textContent = String(count);
    el.toggleAttribute('hidden', count === 0);
  });
}

async function refreshCount() {
  const cart = await fetch(`${root}cart.js`).then((r) => r.json());
  updateBadges(cart.item_count);
  return cart;
}

function open() {
  const d = drawer();
  if (d && typeof d.open === 'function') {
    d.open();
    return true;
  }
  return false;
}

/**
 * @param {FormData | Array<{id: number|string, quantity?: number, properties?: object}>} payload
 * @param {Element} [source] element the add came from (event dispatch target)
 */
async function add(payload, source = document.body) {
  const sections = cartSectionIds().join(',');
  const isForm = payload instanceof FormData;
  const lines = isForm
    ? [{ merchandiseId: String(payload.get('id')), quantity: Number(payload.get('quantity')) || 1 }]
    : payload.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity || 1 }));

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise }));

  let body;
  let headers = { Accept: 'application/json' };
  if (isForm) {
    if (sections) payload.set('sections', sections);
    body = payload;
  } else {
    body = JSON.stringify({ items: payload.map((i) => ({ quantity: 1, ...i })), sections });
    headers = { ...headers, 'Content-Type': 'application/json' };
  }

  try {
    const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers, body }).then((r) => r.json());
    const cart = await refreshCount();
    const failed = Boolean(response.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'nq-cart', itemCount: lines.length, sections: response.sections, didError: failed },
    });
    if (failed) throw new Error(response.description || response.message || 'Could not add to cart');
    // Let the drawer start morphing in the new line before sliding it open.
    setTimeout(() => {
      if (!open()) window.location.href = `${root}cart`;
    }, 60);
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

// Keep the badge right when quantities change or lines are removed inside the drawer.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  /** @type {any} */ (event).promise?.then(() => refreshCount()).catch(() => {});
});

// Header cart icon opens the drawer instead of navigating (falls back to /cart on the cart page).
document.addEventListener('click', (event) => {
  const trigger = /** @type {Element} */ (event.target).closest?.('[data-nq-cart-trigger]');
  if (!trigger || event.metaKey || event.ctrlKey) return;
  if (open()) event.preventDefault();
});

window.NQCart = { add, open, refreshCount };
